import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, LayoutGrid, Loader2, Pause, Play, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import {
  VOICE_PROVIDER_LABELS,
  autoVoiceDesign,
  fetchCreationVoices,
  filterVoices,
  voiceAnswer,
  voiceMeta,
  voiceSampleSource,
} from '@/lib/creationPlan';
import { DesignedVoicePreviews } from '@/components/createPlan/DesignedVoicePreviews';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const GENDER_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
];

export function useVoiceSamples(language) {
  const audioRef = useRef(null);
  const urlsRef = useRef(new Map());
  const [playingKey, setPlayingKey] = useState('');
  const [loadingKey, setLoadingKey] = useState('');

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingKey('');
  }, []);

  useEffect(
    () => () => {
      if (audioRef.current) audioRef.current.pause();
      urlsRef.current.forEach((url) => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    },
    [],
  );

  const toggle = useCallback(
    async (voice) => {
      if (playingKey === voice.key) {
        stop();
        return;
      }
      stop();
      let url = urlsRef.current.get(voice.key);
      if (!url) {
        const source = voiceSampleSource(voice, language);
        if (source.url) {
          url = source.url;
        } else {
          setLoadingKey(voice.key);
          try {
            const { data } = await api.post('/tts/voices/sample', source.body, { responseType: 'blob' });
            url = URL.createObjectURL(data);
          } catch (err) {
            toast.error(formatApiError(err, `Couldn't load a sample for ${voice.name}`));
            return;
          } finally {
            setLoadingKey('');
          }
        }
        urlsRef.current.set(voice.key, url);
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlayingKey((k) => (k === voice.key ? '' : k));
      audio.onerror = () => setPlayingKey((k) => (k === voice.key ? '' : k));
      setPlayingKey(voice.key);
      audio.play().catch(() => setPlayingKey(''));
    },
    [language, playingKey, stop],
  );

  return { playingKey, loadingKey, toggle, stop };
}

export function VoiceRow({ voice, badge, samples, onPick, compact = false }) {
  const playing = samples.playingKey === voice.key;
  const loading = samples.loadingKey === voice.key;
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 hover:border-[#2DD4BF]/40 transition-colors"
      data-testid="create-plan-voice-row"
    >
      <button
        type="button"
        onClick={() => samples.toggle(voice)}
        aria-label={playing ? `Stop ${voice.name}` : `Play a sample of ${voice.name}`}
        className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center cursor-pointer border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 text-[#2DD4BF] hover:bg-[#2DD4BF]/20"
      >
        {loading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : playing ? (
          <Pause className="w-3.5 h-3.5" />
        ) : (
          <Play className="w-3.5 h-3.5 translate-x-px" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-xs font-semibold text-white truncate">{voice.name}</p>
          {badge ? (
            <span className="shrink-0 px-1.5 py-px rounded-full text-[9px] font-semibold bg-[#2DD4BF]/15 text-[#2DD4BF]">
              {badge}
            </span>
          ) : null}
        </div>
        <p className="text-[10px] text-[#9CA3AF] truncate">
          {voiceMeta(voice) || voice.voice_id}
          {!compact && voice.description ? ` · ${voice.description}` : ''}
        </p>
        {!compact ? (
          <p className="text-[9px] text-[#6B7280] truncate font-mono">
            {voice.voice_id} · {VOICE_PROVIDER_LABELS[voice.provider] || voice.provider}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onPick(voice)}
        className="shrink-0 px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer bg-[#2DD4BF] text-[#06080D] flex items-center gap-1 hover:bg-[#5EEAD4]"
        data-testid="create-plan-voice-choose"
      >
        <Check className="w-3 h-3" /> Choose
      </button>
    </div>
  );
}

export function VoiceBrowser({ voices, suggestedKeys, samples, onPick, onClose, prompt }) {
  const [query, setQuery] = useState('');
  const [gender, setGender] = useState('all');
  const shown = useMemo(() => filterVoices(voices, { query, gender }), [voices, query, gender]);

  return createPortal(
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="All voices"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        data-testid="create-plan-voice-popup"
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-white/10 bg-[#0b0e14] shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-white/10">
          <div>
            <p className="text-sm font-semibold text-white">{prompt || 'All voices'}</p>
            <p className="text-[11px] text-[#9CA3AF]">
              {voices.length} voices · play a sample, then choose one
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-white hover:bg-white/10 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-white/10">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 text-[#6B7280] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, accent or voice ID"
              data-testid="create-plan-voice-search"
              className="w-full pl-8 pr-3 py-1.5 rounded-xl text-xs bg-white/[0.04] border border-white/15 text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#2DD4BF]/60"
            />
          </div>
          <div className="flex gap-1">
            {GENDER_FILTERS.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-pressed={gender === g.id}
                onClick={() => setGender(g.id)}
                className={`px-2.5 py-1 rounded-full text-[11px] cursor-pointer border transition-colors ${
                  gender === g.id
                    ? 'border-[#2DD4BF]/70 bg-[#2DD4BF]/15 text-[#2DD4BF]'
                    : 'border-white/15 text-[#E5E7EB] hover:border-[#2DD4BF]/50'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {shown.length ? (
            shown.map((v) => (
              <VoiceRow
                key={v.key}
                voice={v}
                badge={suggestedKeys.has(v.key) ? 'Suggested' : v.native ? 'Native' : ''}
                samples={samples}
                onPick={onPick}
              />
            ))
          ) : (
            <p className="text-xs text-[#9CA3AF] text-center py-8">No voices match.</p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Voice-first narrator picker for the create agent: suggested voices inline, the full catalog in a popup. */
export default function VoicePickerQuestion({ planId, language, prompt, onPick, onAuto, onPlan }) {
  const [state, setState] = useState({
    loading: true,
    error: '',
    suggested: [],
    voices: [],
    language: 'en',
    script_language: null,
  });
  const [browserOpen, setBrowserOpen] = useState(false);
  const samples = useVoiceSamples(state.language);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const data = await fetchCreationVoices(planId, { language });
      setState({
        loading: false,
        error: '',
        language: data.language || 'en',
        suggested: data.suggested || [],
        voices: data.voices || [],
        script_language: data.script_language || null,
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: formatApiError(err, 'Could not load voices') }));
    }
  }, [planId, language]);

  useEffect(() => {
    load();
  }, [load]);

  const pick = (voice) => {
    samples.stop();
    setBrowserOpen(false);
    const { value, label } = voiceAnswer(voice);
    onPick(value, label);
  };

  const suggestedKeys = useMemo(() => new Set(state.suggested.map((v) => v.key)), [state.suggested]);
  const moreCount = state.voices.length - state.suggested.length;

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF]" data-testid="create-plan-voices-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Finding voices that fit your story…
      </div>
    );
  }

  const runAuto = async () => {
    samples.stop();
    try {
      const { plan, voice } = await autoVoiceDesign(planId, { slot: 'narrator' });
      if (plan && onPlan) onPlan(plan);
      if (voice?.voice_id) {
        const { value, label } = voiceAnswer({
          provider: voice.provider || 'elevenlabs',
          voice_id: voice.voice_id,
          name: voice.name || voice.voice_id,
          key: `elevenlabs:${voice.voice_id}`,
        });
        onPick(value, label);
        return;
      }
    } catch {
      /* fall through */
    }
    onAuto();
  };

  const autoButton = (
    <button
      type="button"
      onClick={() => {
        runAuto();
      }}
      data-testid="create-plan-voice-auto"
      className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-dashed border-white/20 text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white inline-flex items-center gap-1"
    >
      <TwinneeLogo className="w-3 h-3" /> Let Twinnee pick
    </button>
  );

  const designed = (
    <DesignedVoicePreviews
      planId={planId}
      slot="narrator"
      title="Design a narrator voice"
      onCommitted={(voice, plan) => {
        if (plan && onPlan) onPlan(plan);
        const { value, label } = voiceAnswer({
          provider: voice.provider || 'elevenlabs',
          voice_id: voice.voice_id,
          name: voice.name || voice.voice_id,
          key: `elevenlabs:${voice.voice_id}`,
        });
        onPick(value, label);
      }}
    />
  );

  if (state.error || !state.voices.length) {
    return (
      <div className="space-y-2" data-testid="create-plan-voices-error">
        {designed}
        <p className="text-[11px] text-[#9CA3AF]">{state.error || 'No narrator voices are configured on this server.'}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-white/20 text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
          {autoButton}
        </div>
      </div>
    );
  }

  const detectedLabel = (() => {
    const det = state.script_language;
    if (!det?.preferred) return '';
    const code = String(det.preferred);
    const names = { en: 'English', hi: 'Hindi', es: 'Spanish', fr: 'French', de: 'German', ta: 'Tamil', te: 'Telugu', bn: 'Bengali', mr: 'Marathi' };
    return names[code] || code;
  })();

  return (
    <div className="space-y-2 max-w-md" data-testid="create-plan-voice-picker">
      {designed}
      {detectedLabel ? (
        <p className="text-[11px] text-[#9CA3AF]" data-testid="create-plan-voice-script-language">
          Voices ranked for {detectedLabel}
          {state.script_language?.english_deprioritized ? ' (English deprioritized — mixed script)' : ''}
          {' '}from your script.
        </p>
      ) : null}
      {state.suggested.map((v, i) => (
        <VoiceRow key={v.key} voice={v} badge={i === 0 ? 'Best match' : ''} samples={samples} onPick={pick} compact />
      ))}
      <div className="flex flex-wrap gap-2 pt-0.5">
        {moreCount > 0 ? (
          <button
            type="button"
            onClick={() => setBrowserOpen(true)}
            data-testid="create-plan-voice-more"
            className="px-3 py-1.5 rounded-full text-xs cursor-pointer border border-white/20 text-[#E5E7EB] hover:border-[#2DD4BF]/50 hover:text-white inline-flex items-center gap-1"
          >
            <LayoutGrid className="w-3 h-3" /> More voices · +{moreCount}
          </button>
        ) : null}
        {autoButton}
      </div>
      {browserOpen ? (
        <VoiceBrowser
          voices={state.voices}
          suggestedKeys={suggestedKeys}
          samples={samples}
          onPick={pick}
          onClose={() => setBrowserOpen(false)}
          prompt={prompt}
        />
      ) : null}
    </div>
  );
}
